import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';
import Breadcrumb from '../../../components/Breadcrumb';

export default function MilwaukeePage() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Technelle AI Marketing - Milwaukee',
    description:
      'AI-driven marketing agency serving Milwaukee businesses with advanced automation, lead generation, and growth strategies.',
    url: 'https://technelle.ai/service-areas/milwaukee',
    telephone: '414-530-9114',
    email: 'melissa@technelle.ai',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '2810 Crossroads Dr Suite 4000',
      addressLocality: 'Waukesha',
      addressRegion: 'WI',
      postalCode: '53189',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '43.0389',
      longitude: '-88.2315',
    },
    areaServed: {
      '@type': 'City',
      name: 'Milwaukee',
      '@id': 'https://en.wikipedia.org/wiki/Milwaukee',
    },
    priceRange: '$$',
    openingHours: 'Mo-Fr 09:00-17:00',
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SEOHead
        title="AI Marketing Agency Milwaukee, WI | Wisconsin Business Growth | Technelle"
        description="Leading AI marketing agency serving Milwaukee, WI businesses. Drive Wisconsin business growth with advanced AI automation, lead generation, and data-driven strategies. 20+ years experience helping Milwaukee companies scale."
        keywords="AI marketing Milwaukee WI, marketing agency Milwaukee Wisconsin, AI automation Milwaukee, lead generation Milwaukee, Wisconsin business growth Milwaukee"
        canonical="https://technelle.ai/service-areas/milwaukee"
        ogImage="https://public.readdy.ai/ai/img_res/45cab8f1a6f8a08c5d8e4fc881af740f.jpg"
        structuredData={structuredData}
        geoPosition="43.0389;-88.2315"
        geoRegion="US-WI"
        geoPlacename="Milwaukee"
        breadcrumbs={[
          { name: 'Home', url: 'https://technelle.ai/' },
          { name: 'Service Areas', url: 'https://technelle.ai/service-areas' },
          { name: 'Milwaukee', url: 'https://technelle.ai/service-areas/milwaukee' }
        ]}
      />

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">T</span>
                </div>
                <span className="text-xl font-bold text-gray-900">Technelle</span>
              </Link>
              <div className="hidden md:flex items-center gap-8">
                <Link
                  to="/services"
                  className="text-gray-600 hover:text-blue-500 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  Services
                </Link>
                <Link
                  to="/industries"
                  className="text-gray-600 hover:text-blue-500 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  Industries
                </Link>
                <Link
                  to="/about"
                  className="text-gray-600 hover:text-blue-500 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  About
                </Link>
                <Link
                  to="/resources"
                  className="text-gray-600 hover:text-blue-500 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  Resources
                </Link>
                <Link
                  to="/contact"
                  className="bg-blue-500 text-white px-6 py-2.5 rounded-full hover:bg-blue-600 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  Book Free Session
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Breadcrumb */}
        <div className="pt-24 pb-4">
          <Breadcrumb />
        </div>

        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Milwaukee%20Wisconsin%20downtown%20skyline%20with%20modern%20business%20district%20and%20Lake%20Michigan%20waterfront%2C%20professional%20corporate%20photography%20style%20with%20clear%20blue%20sky%20and%20urban%20architecture%20representing%20thriving%20business%20community%20and%20economic%20growth%20opportunities%20for%20local%20businesses&width=1920&height=800&seq=milwaukee-hero-002&orientation=landscape"
              alt="Milwaukee Wisconsin downtown skyline - AI marketing services for Milwaukee businesses"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="max-w-3xl">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                AI Marketing Agency Serving Milwaukee Businesses
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Drive explosive growth for your Milwaukee business with cutting‑edge AI marketing automation, advanced lead generation, and data‑driven strategies. We help Milwaukee companies scale faster with proven AI solutions.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="bg-blue-500 text-white px-8 py-4 rounded-full hover:bg-blue-600 transition-colors text-lg font-semibold whitespace-nowrap inline-block"
                >
                  Book Your Free Strategy Session
                </Link>
                <a
                  href="tel:414-530-9114"
                  className="bg-white text-gray-900 px-8 py-4 rounded-full hover:bg-gray-100 transition-colors text-lg font-semibold whitespace-nowrap inline-block"
                >
                  Call (414) 530-9114
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-blue-500 mb-2">20+</div>
                <div className="text-sm text-gray-600">Years Experience</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-500 mb-2">1,000+</div>
                <div className="text-sm text-gray-600">Projects Completed</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-500 mb-2">40,000+</div>
                <div className="text-sm text-gray-600">Hours of Expertise</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-500 mb-2">3x</div>
                <div className="text-sm text-gray-600">Average ROI</div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Milwaukee Businesses Choose Us */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Milwaukee Businesses Choose Technelle
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We understand the unique challenges and opportunities facing Milwaukee
                businesses. Our AI‑driven marketing solutions are tailored to help you
                dominate your local market and expand beyond.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:border-blue-200 transition-colors">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-map-pin-line text-2xl text-blue-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Local Milwaukee Expertise
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Based in nearby Waukesha, we deeply understand the Milwaukee market,
                  from Bay View to Wauwatosa. We know what resonates with Milwaukee
                  customers and how to position your business for maximum local impact
                  and growth.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:border-blue-200 transition-colors">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-robot-line text-2xl text-blue-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Advanced AI Technology
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Leverage cutting‑edge AI marketing automation, predictive analytics,
                  and machine learning to outperform competitors. Our AI solutions help
                  Milwaukee businesses generate more qualified leads, close more deals,
                  and scale faster than traditional marketing methods.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:border-blue-200 transition-colors">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-line-chart-line text-2xl text-blue-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Proven Results
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our Milwaukee clients see an average 3x ROI within the first year.
                  We've helped local businesses increase leads by 35%, boost
                  conversions by 62%, and expand their pipeline by 3.1x using our
                  proven AI marketing strategies and automation systems.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services for Milwaukee */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                AI Marketing Services for Milwaukee
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive AI‑driven marketing solutions designed to help Milwaukee
                businesses dominate their market and achieve sustainable growth.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-2xl border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  AI Lead Generation
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Generate high‑quality leads automatically with AI‑powered targeting,
                  personalization, and nurturing. Our systems identify and engage your
                  ideal Milwaukee customers 24/7, filling your pipeline with qualified
                  prospects ready to buy.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      Predictive lead scoring and qualification
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      Automated multi‑channel outreach campaigns
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      AI chatbots for instant lead capture and qualification
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Marketing Automation
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Automate repetitive marketing tasks and scale your Milwaukee business
                  without adding headcount. Our AI automation systems handle email
                  campaigns, social media, content distribution, and customer nurturing
                  while you focus on closing deals.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      Intelligent email marketing automation
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      Social media scheduling and optimization
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      Personalized customer journey automation
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Account‑Based Marketing (ABM)
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Target high‑value Milwaukee accounts with precision using AI‑powered
                  ABM strategies. We help B2B companies identify, engage, and close their
                  ideal customers with personalized campaigns that drive revenue growth
                  and shorten sales cycles.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      AI‑powered account identification and prioritization
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">Personalized multi‑touch campaigns</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      Real‑time engagement tracking and optimization
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  AI Content Marketing
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Create and distribute high‑performing content at scale with AI
                  assistance. Our content marketing strategies help Milwaukee businesses
                  establish thought leadership, improve SEO rankings, and attract
                  qualified leads through valuable, engaging content.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      AI‑assisted content creation and optimization
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      SEO‑optimized blog posts and articles
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700">
                      Content performance analytics and insights
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Milwaukee Industries We Serve */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Milwaukee Industries We Serve
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We specialize in helping Milwaukee businesses across diverse industries
                leverage AI marketing to achieve breakthrough growth and competitive
                advantage.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Manufacturing</h3>
                <p className="text-sm text-gray-600">
                  Milwaukee's manufacturing sector benefits from our B2B lead generation
                  and ABM strategies.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Healthcare</h3>
                <p className="text-sm text-gray-600">
                  Patient acquisition and retention through compliant AI marketing
                  automation.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Professional Services</h3>
                <p className="text-sm text-gray-600">
                  Law firms, accounting, consulting - we help you attract high‑value
                  clients.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Technology &amp; SaaS
                </h3>
                <p className="text-sm text-gray-600">
                  Scale your Milwaukee tech startup with AI‑powered growth marketing.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Real Estate</h3>
                <p className="text-sm text-gray-600">
                  Generate qualified buyer and seller leads in the competitive Milwaukee
                  market.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Hospitality</h3>
                <p className="text-sm text-gray-600">
                  Hotels, restaurants, events - fill your calendar with AI marketing
                  automation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-blue-500 to-blue-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Dominate the Milwaukee Market?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Book your free strategy session today and discover how AI marketing can
              transform your Milwaukee business. Limited to 5 sessions per month.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-blue-500 px-8 py-4 rounded-full hover:bg-gray-100 transition-colors text-lg font-semibold whitespace-nowrap inline-block"
              >
                Book My Free Strategy Session
              </Link>
              <a
                href="tel:414-530-9114"
                className="bg-blue-600 text-white px-8 py-4 rounded-full hover:bg-blue-700 transition-colors text-lg font-semibold whitespace-nowrap inline-block border-2 border-white"
              >
                Call (414) 530-9114
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-4 gap-12 mb-12">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">T</span>
                  </div>
                  <span className="text-xl font-bold">Technelle</span>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  AI‑driven marketing agency helping Milwaukee businesses achieve
                  breakthrough growth through advanced automation and data‑driven
                  strategies.
                </p>
              </div>

              <div>
                <h4 className="font-bold mb-4 text-white">Services</h4>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/services"
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                    >
                      AI Marketing
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services"
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                    >
                      Lead Generation
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services"
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                    >
                      Marketing Automation
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services"
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                    >
                      Account-Based Marketing
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold mb-4 text-white">Service Areas</h4>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/service-areas/milwaukee"
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                    >
                      Milwaukee
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/service-areas/madison"
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                    >
                      Madison
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/service-areas/green-bay"
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                    >
                      Green Bay
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/about"
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                    >
                      Waukesha
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold mb-4 text-white">Contact</h4>
                <ul className="space-y-3">
                  <li className="text-gray-400 text-sm">
                    2810 Crossroads Dr Suite 4000
                  </li>
                  <li className="text-gray-400 text-sm">
                    Waukesha, WI 53189
                  </li>
                  <li>
                    <a
                      href="tel:414-530-9114"
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                    >
                      (414) 530-9114
                    </a>
                  </li>
                  <li>
                    <a
                      href="mailto:melissa@technelle.ai"
                      className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                    >
                      melissa@technelle.ai
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">© 2024 Technelle. All rights reserved.</p>

            </div>
          </div>
        </footer>
      </div>
    </Suspense>
  );
}
